import React, { useMemo, useState } from "react";
import { Calculator } from "lucide-react";
import { Btn, SectionHeading } from "./ui";
import { Reveal } from "./useReveal";
import { LINKS } from "./data";

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Slider = ({ label, value, min, max, step, onChange, display, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-medium text-[#2D2D37]/70">{label}</span>
      <span className="font-heading font-bold text-[#254369]">{display}</span>
    </div>
    <input
      type="range"
      data-testid={testid}
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-2 rounded-full appearance-none cursor-pointer bg-[#E5E5E5] accent-[#C9A961]"
    />
  </div>
);

export const MortgageCalculator = () => {
  const [price, setPrice] = useState(850000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);

  const { monthly, loan, down } = useMemo(() => {
    const down = (price * downPct) / 100;
    const loan = price - down;
    const r = rate / 100 / 12;
    const n = term * 12;
    const m = r === 0 ? loan / n : (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return { monthly: Math.round(m), loan, down };
  }, [price, downPct, rate, term]);

  return (
    <section id="calculator" className="py-16 lg:py-24 bg-[#F5F5F5]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Plan Ahead"
            title="Estimate Your Monthly Payment"
            subtitle="Get a quick estimate of a monthly mortgage payment, then talk with Wallstreet Realty to explore your real financing options."
            center
          />
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-10 grid lg:grid-cols-2 gap-6 items-stretch">
            <div className="bg-white border border-[#E5E5E5] rounded-[20px] p-8 space-y-7">
              <Slider label="Home Price" value={price} min={200000} max={3000000} step={10000}
                onChange={setPrice} display={fmt(price)} testid="calc-price" />
              <Slider label="Down Payment" value={downPct} min={0} max={50} step={1}
                onChange={setDownPct} display={`${downPct}% · ${fmt(down)}`} testid="calc-down" />
              <Slider label="Interest Rate" value={rate} min={2} max={10} step={0.1}
                onChange={setRate} display={`${rate.toFixed(1)}%`} testid="calc-rate" />
              <Slider label="Loan Term" value={term} min={10} max={30} step={5}
                onChange={setTerm} display={`${term} years`} testid="calc-term" />
            </div>

            <div className="bg-[#254369] rounded-[20px] p-8 flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mb-5">
                <Calculator className="text-[#C9A961]" size={26} />
              </div>
              <p className="text-white/70 text-sm uppercase tracking-[0.16em] font-medium">Estimated Monthly Payment</p>
              <p data-testid="calc-result" className="font-heading font-extrabold text-white text-5xl sm:text-6xl mt-2">
                {fmt(monthly)}
              </p>
              <p className="text-white/60 text-sm mt-3">
                Principal &amp; interest on a {fmt(loan)} loan
              </p>
              <p className="text-white/40 text-xs mt-4 max-w-xs leading-relaxed">
                Estimate only. Excludes taxes, insurance, and HOA. Not a loan offer or commitment.
              </p>
              <div className="mt-6">
                <Btn href={LINKS.buyers} variant="gold" testid="calc-financing-cta">
                  Get Financing Guidance
                </Btn>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
